import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Phone, Mail, MapPin, Globe, Building2, Trash2, Filter, X
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import ContactForm from '@/components/forms/ContactForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Fixed color palette per category (consistent visual identity)
const CATEGORY_STYLES = {
  'Loueur de benne / Déchets':   { bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-200' },
  'Transport / Livraison':        { bg: 'bg-blue-50',   text: 'text-blue-700',   ring: 'ring-blue-200' },
  'Location matériel / Outillage':{ bg: 'bg-amber-50',  text: 'text-amber-700',  ring: 'ring-amber-200' },
  'Fournisseur matériaux':        { bg: 'bg-emerald-50',text: 'text-emerald-700',ring: 'ring-emerald-200' },
  'Assurance':                    { bg: 'bg-indigo-50', text: 'text-indigo-700', ring: 'ring-indigo-200' },
  'Expert-comptable':             { bg: 'bg-purple-50', text: 'text-purple-700', ring: 'ring-purple-200' },
  'Avocat / Juridique':           { bg: 'bg-rose-50',   text: 'text-rose-700',   ring: 'ring-rose-200' },
  'Administration / Mairie':      { bg: 'bg-sky-50',    text: 'text-sky-700',    ring: 'ring-sky-200' },
  'Banque':                       { bg: 'bg-teal-50',   text: 'text-teal-700',   ring: 'ring-teal-200' },
  'Nettoyage / Fin de chantier':  { bg: 'bg-cyan-50',   text: 'text-cyan-700',   ring: 'ring-cyan-200' },
  'Gardiennage / Sécurité':       { bg: 'bg-slate-100', text: 'text-slate-700',  ring: 'ring-slate-200' },
  'Autre':                        { bg: 'bg-slate-50',  text: 'text-slate-600',  ring: 'ring-slate-200' },
};

const styleFor = (cat) => CATEGORY_STYLES[cat] || CATEGORY_STYLES['Autre'];

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [categorieFilter, setCategorieFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Contacts utiles - Reno Resolve';
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contacts');
      setContacts(data || []);
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
      toast.error('Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditing(null);
    fetchContacts();
    toast.success(editing ? 'Contact mis à jour' : 'Contact créé');
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Supprimer ${contact.entreprise || contact.nom_prenom} ?`)) return;
    try {
      await api.delete(`/contacts/${contact.contact_id}`);
      toast.success('Contact supprimé');
      fetchContacts();
    } catch (e) {
      toast.error('Suppression impossible');
    }
  };

  // Compute available categories + per-category counts
  const { uniqueCategories, filtered } = useMemo(() => {
    const cats = new Set();
    contacts.forEach((c) => c.categorie && cats.add(c.categorie));
    const q = search.trim().toLowerCase();
    const f = contacts.filter((c) => {
      const catOk = categorieFilter === 'all' || c.categorie === categorieFilter;
      if (!catOk) return false;
      if (!q) return true;
      return [c.entreprise, c.nom_prenom, c.email, c.telephone, c.ville, c.categorie]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    return { uniqueCategories: [...cats].sort(), filtered: f };
  }, [contacts, search, categorieFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-10">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Contacts utiles
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Loueurs de benne, transporteurs, fournisseurs, partenaires &mdash; tous les contacts utiles au quotidien.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="create-contact-btn"
          className="w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau contact
        </Button>
      </div>

      {/* Search + category filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par entreprise, nom, ville, téléphone…"
            className="pl-10"
            data-testid="contacts-search"
          />
        </div>
        {uniqueCategories.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setCategorieFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                categorieFilter === 'all'
                  ? 'bg-slate-900 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="filter-cat-all"
            >
              Tous ({contacts.length})
            </button>
            {uniqueCategories.map((cat) => {
              const count = contacts.filter((c) => c.categorie === cat).length;
              const st = styleFor(cat);
              const active = categorieFilter === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setCategorieFilter(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-slate-900 text-white'
                      : `${st.bg} ${st.text} hover:ring-2 ${st.ring}`
                  }`}
                >
                  {cat} ({count})
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">
            {contacts.length === 0 ? 'Aucun contact utile pour le moment' : 'Aucun résultat'}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {contacts.length === 0
              ? 'Ajoutez vos loueurs de benne, transporteurs, fournisseurs et autres partenaires récurrents.'
              : 'Essayez un autre mot-clé ou changez de catégorie.'}
          </p>
          {contacts.length === 0 && (
            <Button className="mt-4" onClick={() => setShowForm(true)} data-testid="empty-create-contact-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un contact
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const st = styleFor(c.categorie);
            return (
              <div
                key={c.contact_id}
                className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all group"
                data-testid={`contact-card-${c.contact_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    {c.categorie && (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${st.bg} ${st.text} mb-1.5`}>
                        {c.categorie}
                      </span>
                    )}
                    <h3 className="font-semibold text-slate-900 truncate">
                      {c.entreprise || c.nom_prenom}
                    </h3>
                    {c.entreprise && (
                      <p className="text-xs text-slate-500 truncate">{c.nom_prenom}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(c)}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition"
                    aria-label="Supprimer"
                    data-testid={`delete-contact-${c.contact_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-1.5 text-sm text-slate-600">
                  {c.telephone && (
                    <a href={`tel:${c.telephone}`} className="flex items-center gap-2 hover:text-blue-700">
                      <Phone className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{c.telephone}</span>
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-blue-700">
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{c.email}</span>
                    </a>
                  )}
                  {c.site_web && (
                    <a
                      href={c.site_web.startsWith('http') ? c.site_web : `https://${c.site_web}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 hover:text-blue-700"
                    >
                      <Globe className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{c.site_web.replace(/^https?:\/\//, '')}</span>
                    </a>
                  )}
                  {(c.ville || c.code_postal) && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">
                        {[c.code_postal, c.ville].filter(Boolean).join(' ')}
                      </span>
                    </div>
                  )}
                </div>

                {c.notes && (
                  <p className="text-xs text-slate-500 mt-3 line-clamp-2 border-t border-slate-100 pt-2 italic">
                    {c.notes}
                  </p>
                )}

                <div className="flex gap-2 mt-3 pt-2 border-t border-slate-100">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setEditing(c); setShowForm(true); }}
                    data-testid={`edit-contact-${c.contact_id}`}
                  >
                    Modifier
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier le contact' : 'Nouveau contact utile'}</DialogTitle>
          </DialogHeader>
          <ContactForm
            contact={editing}
            onSuccess={handleSaved}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
